export type ScrollTarget = string | HTMLElement

function resolveTargetElement(target: ScrollTarget) {
  if (typeof target === 'string') return document.querySelector<HTMLElement>(target)
  if (target instanceof HTMLElement) return target
  return null
}

function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement
  while (parent) {
    const { overflowY } = getComputedStyle(parent)
    const isScrollable = (overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight
    if (isScrollable) return parent
    parent = parent.parentElement
  }
  return null
}

function scrollTargetToCenter(target: ScrollTarget, offset: number = 0) {
  const el = resolveTargetElement(target)
  if (!el || !el.isConnected) return

  const container = findScrollableAncestor(el)

  if (!container) {
    const rect = el.getBoundingClientRect()
    const elementCenter = rect.top + window.pageYOffset + rect.height / 2
    const viewportCenter = window.innerHeight / 2
    window.scrollTo({
      top: elementCenter - viewportCenter - offset / 2,
      behavior: 'smooth',
    })
    return
  }

  const cRect = container.getBoundingClientRect()
  const eRect = el.getBoundingClientRect()

  // 這裡減去 offset 是為了讓元素位置「往下移」，避開頂部遮擋
  const scrollTop = container.scrollTop + (eRect.top - cRect.top) - (cRect.height - eRect.height) / 2 - offset / 2

  container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' })
}

// ─── Exports ────
export function scrollToTop(element = window, behavior: ScrollBehavior = 'smooth') {
  if (window === undefined || element.scrollTo === undefined) {
    return
  }

  element.scrollTo({ top: 0, behavior })
}

// 找到 target 後 scroll 到中央，回傳 cancel function
export function scrollToTargetAfterModalEnter(target: ScrollTarget, offset: number = 0): () => void {
  let cancelled = false
  const cancel = () => {
    cancelled = true
  }

  let attempts = 0
  const tryScroll = () => {
    if (cancelled) return
    const el = resolveTargetElement(target)
    if (!el?.isConnected) {
      if (++attempts < 30) setTimeout(tryScroll, 100)
      return
    }
    requestAnimationFrame(() => {
      if (!cancelled) scrollTargetToCenter(target, offset)
    })
  }
  tryScroll()

  return cancel
}

export function scrollToTargetAfterEnter(selector: string, offset: number = 0): () => void {
  return scrollToTargetAfterModalEnter(selector, offset)
}
